import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { ConfigService } from './config.service';

/**
 * Status of an Antigravity OAuth auth session
 */
export type AntigravityAuthStatus =
  | 'starting'
  | 'waiting_for_auth'  // OAuth URL generated, waiting for user to paste code
  | 'authenticated'
  | 'failed'
  | 'cancelled';

export interface AntigravityAuthSessionResponse {
  session_id: string;
  status: AntigravityAuthStatus;
  oauth_url: string | null;
  error?: string | null;
}

export interface AntigravitySubmitCodeResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Service for Antigravity (agy) Google OAuth authentication.
 *
 * Flow:
 * 1. startAuth() → backend returns Google OAuth URL
 * 2. User opens URL, signs in with Google
 * 3. Google redirects to antigravity.google/oauth-callback → shows the code
 * 4. User copies code and pastes it in our UI
 * 5. submitCode() → backend exchanges for tokens and writes agy's token file
 */
@Injectable({
  providedIn: 'root'
})
export class AntigravityAuthWizardService {
  private _sessionId: string | null = null;

  private _authStatus = new BehaviorSubject<AntigravityAuthStatus | null>(null);
  public authStatus$ = this._authStatus.asObservable();

  private _error = new BehaviorSubject<string | null>(null);
  public error$ = this._error.asObservable();

  constructor(private http: HttpClient) {}

  get sessionId(): string | null {
    return this._sessionId;
  }

  get currentStatus(): AntigravityAuthStatus | null {
    return this._authStatus.value;
  }

  get isAuthInProgress(): boolean {
    const status = this._authStatus.value;
    return status === 'starting' || status === 'waiting_for_auth';
  }

  async startAuth(): Promise<AntigravityAuthSessionResponse> {
    this._sessionId = null;
    this._error.next(null);
    this._authStatus.next('starting');

    try {
      const response = await lastValueFrom(
        this.http.post<AntigravityAuthSessionResponse>(
          `${ConfigService.muxUrl}/backends/antigravity/auth/start`,
          {}
        )
      );

      this._sessionId = response.session_id;
      this._authStatus.next(response.status as AntigravityAuthStatus);

      if (response.error) {
        this._error.next(response.error);
      }

      return response;
    } catch (error: any) {
      this._authStatus.next('failed');
      const errorMsg = error?.error?.detail || error?.message || 'Failed to start auth';
      this._error.next(errorMsg);
      throw error;
    }
  }

  async submitCode(code: string): Promise<AntigravitySubmitCodeResponse> {
    if (!this._sessionId) {
      return { success: false, error: 'No active auth session' };
    }

    try {
      const response = await lastValueFrom(
        this.http.post<AntigravitySubmitCodeResponse>(
          `${ConfigService.muxUrl}/backends/antigravity/auth/submit-code`,
          { session_id: this._sessionId, code }
        )
      );

      if (response.success) {
        this._authStatus.next('authenticated');
      } else {
        this._error.next(response.message || 'Code exchange failed');
      }

      return response;
    } catch (error: any) {
      let errorMsg = 'Failed to submit code';
      if (error?.error?.detail) {
        errorMsg = Array.isArray(error.error.detail)
          ? error.error.detail.map((d: any) => d.msg || d).join(', ')
          : String(error.error.detail);
      } else if (error?.message) {
        errorMsg = error.message;
      }
      this._error.next(errorMsg);
      return { success: false, error: errorMsg };
    }
  }

  async cancelAuth(): Promise<void> {
    if (!this._sessionId) return;

    try {
      await lastValueFrom(
        this.http.post(
          `${ConfigService.muxUrl}/backends/antigravity/auth/cancel`,
          null,
          { params: { session_id: this._sessionId } }
        )
      );
    } catch (error) {
      console.error('Failed to cancel Antigravity auth:', error);
    }

    this._sessionId = null;
    this._authStatus.next('cancelled');
  }

  async deauth(): Promise<void> {
    try {
      await lastValueFrom(
        this.http.delete(`${ConfigService.muxUrl}/backends/antigravity/auth`)
      );
    } catch (error) {
      console.error('Failed to deauth Antigravity:', error);
    }
  }

  reset(): void {
    this._sessionId = null;
    this._authStatus.next(null);
    this._error.next(null);
  }
}
